import React, { createContext, ReactNode, useCallback, useContext } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useNavigate } from 'react-router-dom';

import { useErrorHandler } from '@futrli/errors';
import { getCallbackUrl, getProductUrl, PREDICT_PRODUCT_SLUG, ROUTES, useModal } from '@futrli/shared-services';
import {
  ExtendedOrganisationListItem,
  ExtendedWorkspaceListItem
} from '@futrli/views/operations/workspaces/processors/workspacesList';
import { ModifyWorkspaceView } from '@futrli/views/views/workspaces/ModifyWorkspaceView/ModifyWorkspaceView';

import { DeleteOrganisationDialog } from '../../dialogs/delete-organisation-dialog/delete-organisation-dialog';
import { OrganisationActivationSidebar } from '../../dialogs/organisation-activation-sidebar/organisation-activation-sidebar';
import { RemoveAccessDialog } from '../../dialogs/remove-access-dialog/remove-access-dialog';
import { ResubscribeSfaOrgDialog } from '../../dialogs/resubscribe-sfa-org-dialog/resubscribe-sfa-org-dialog';
import { UpdateValuesDrawer } from '../../components/update-values-drawer/update-values-drawer';

interface OrganisationsActionsContextValue {
  handleDeleteOrganisationClick: (
    workspace: ExtendedWorkspaceListItem,
    organisation: ExtendedOrganisationListItem
  ) => void;
  handleReconnectOrganisationClick: (
    workspace: ExtendedWorkspaceListItem,
    organisation: ExtendedOrganisationListItem
  ) => void;
  handleRemoveAccessClick: (workspace: ExtendedWorkspaceListItem, organisation: ExtendedOrganisationListItem) => void;
  handleResubscribeOrganisationClick: (organisation: ExtendedOrganisationListItem) => void;
  handleUpdateValuesClick: (workspace: ExtendedWorkspaceListItem, organisation: ExtendedOrganisationListItem) => void;
  handleOrganisationNameClick: (
    workspace: ExtendedWorkspaceListItem,
    organisation: ExtendedOrganisationListItem
  ) => void;
}

const OrganisationsActionsContext = createContext<OrganisationsActionsContextValue>(
  ({} as unknown) as OrganisationsActionsContextValue
);

export const OrganisationsActionsProvider = ({ children }: { children: ReactNode }) => {
  const flags = useFlags();
  const handleError = useErrorHandler();
  const navigate = useNavigate();
  const organisationActivationSidebar = useModal();
  const deleteOrganisationDialog = useModal();
  const removeAccessDialog = useModal();
  const resubscribeSfaOrgDialog = useModal();
  const updateValuesDrawer = useModal();

  const [workspace, setWorkspace] = React.useState<ExtendedWorkspaceListItem>();
  const [organisation, setOrganisation] = React.useState<ExtendedOrganisationListItem>();

  const handleDeleteOrganisationClick = useCallback(
    (workspace: ExtendedWorkspaceListItem, organisation: ExtendedOrganisationListItem) => {
      setWorkspace(workspace);
      setOrganisation(organisation);
      deleteOrganisationDialog.open();
    },
    [deleteOrganisationDialog]
  );

  const handleResubscribeOrganisationClick = useCallback(
    (organisation: ExtendedOrganisationListItem) => {
      setOrganisation(organisation);
      resubscribeSfaOrgDialog.open();
    },
    [resubscribeSfaOrgDialog]
  );

  const handleRemoveAccessClick = useCallback(
    (workspace: ExtendedWorkspaceListItem, organisation: ExtendedOrganisationListItem) => {
      setWorkspace(workspace);
      setOrganisation(organisation);
      removeAccessDialog.open();
    },
    [removeAccessDialog]
  );

  const handleUpdateValuesClick = useCallback(
    (workspace: ExtendedWorkspaceListItem, organisation: ExtendedOrganisationListItem) => {
      setWorkspace(workspace);
      setOrganisation(organisation);
      updateValuesDrawer.open();
    },
    [updateValuesDrawer]
  );

  const handleReconnectOrganisationClick = useCallback(
    async (workspace: ExtendedWorkspaceListItem, organisation: ExtendedOrganisationListItem) => {
      const view = new ModifyWorkspaceView(flags, workspace.id);

      try {
        const response = await view.reconnectOrganisation(organisation.id, getCallbackUrl(window.location.href));

        if (response.status === 'SUCCESS' && response.initiation_url) {
          window.location.href = response.initiation_url;
        }
      } catch {
        handleError(true);
      } finally {
        view.destroy();
      }
    },
    [flags, handleError]
  );

  const handleOrganisationNameClick = useCallback(
    (workspace: ExtendedWorkspaceListItem, organisation: ExtendedOrganisationListItem) => {
      if (organisation.can_be_accessed === false) {
        return;
      }

      if (workspace.subscription_state === 'inactive') {
        setWorkspace(workspace);
        setOrganisation(organisation);
        organisationActivationSidebar.open();
        return;
      }

      navigate(`${getProductUrl(workspace.id, organisation.id, PREDICT_PRODUCT_SLUG)}${ROUTES.HOME}`);
    },
    [navigate, organisationActivationSidebar]
  );

  const value = {
    handleDeleteOrganisationClick,
    handleReconnectOrganisationClick,
    handleRemoveAccessClick,
    handleOrganisationNameClick,
    handleResubscribeOrganisationClick,
    handleUpdateValuesClick
  };

  return (
    <OrganisationsActionsContext.Provider value={value}>
      {workspace && organisation && (
        <>
          {organisationActivationSidebar.isOpen && (
            <OrganisationActivationSidebar
              isOpen={organisationActivationSidebar.isOpen}
              onClose={organisationActivationSidebar.close}
              workspaceId={workspace.id}
              organisationId={organisation.id}
            />
          )}
          <DeleteOrganisationDialog
            isOpen={deleteOrganisationDialog.isOpen}
            onClose={deleteOrganisationDialog.close}
            workspaceId={workspace.id}
            organisationId={organisation.id}
          />
        </>
      )}

      {workspace && organisation && (
        <RemoveAccessDialog
          isOpen={removeAccessDialog.isOpen}
          isWorkspaceSuperAdmin={workspace.flags.isWorkspaceSuperAdmin}
          onClose={removeAccessDialog.close}
          workspaceId={workspace.id}
          organisationId={organisation.id}
        />
      )}

      {organisation && (
        <ResubscribeSfaOrgDialog
          isOpen={resubscribeSfaOrgDialog.isOpen}
          onClose={resubscribeSfaOrgDialog.close}
          organisation={organisation}
        />
      )}

      {workspace && organisation && (
        <UpdateValuesDrawer
          isOpen={updateValuesDrawer.isOpen}
          onClose={updateValuesDrawer.close}
          organisation={organisation}
          workspace={workspace}
        />
      )}

      {children}
    </OrganisationsActionsContext.Provider>
  );
};

export const useOrganisationsActions = () => {
  const context = useContext(OrganisationsActionsContext);
  if (!context) {
    throw new Error('useOrganisationsActions must be used within an OrganisationsActionsProvider');
  }
  return context;
};
